import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import {
  FINANCIAL_ENTRY_STATUS_LABELS,
  FISCAL_DOCUMENT_STATUS_LABELS,
  PIX_KEY_KIND_LABELS,
  PURCHASE_ORDER_STATUS_LABELS,
  formatCents,
  type Paginated,
  type Person,
  type PersonSummary,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { blank, formatCep, formatDate, formatDocument, formatPhone } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { Attachments } from '@/components/Attachments';
import { ActiveBadge, StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { PersonFormDrawer } from '@/routes/cadastros/Pessoas';

const ENTRY_TONE: Record<string, BadgeKind> = {
  OPEN: 'info',
  PARTIALLY_PAID: 'warn',
  PAID: 'ok',
  CANCELLED: 'mute',
};

const ORDER_TONE: Record<string, BadgeKind> = {
  RASCUNHO: 'mute',
  ENVIADO: 'info',
  CONFIRMADO: 'info',
  PARCIALMENTE_RECEBIDO: 'warn',
  RECEBIDO: 'ok',
  CANCELADO: 'mute',
};

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** `YYYY-MM` → "set/26". Sem Date no meio: mês é texto, não instante. */
function mesCurto(month: string): string {
  const [ano, mes] = month.split('-');
  return `${MESES[Number(mes) - 1] ?? mes}/${(ano ?? '').slice(2)}`;
}

/**
 * Fornecedores — dentro do Financeiro de propósito.
 *
 * É o mesmo cadastro de Pessoas (papel FORNECEDOR), mas visto de onde o
 * dinheiro sai: a lista é de quem vende para o parque, e o painel de cada um
 * responde "quanto já gastei com ele, quanto ainda devo, o que ele me vendeu
 * por último" antes de qualquer campo de endereço.
 */
export function FornecedoresPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Person | null>(null);
  const [creating, setCreating] = useState(false);

  const listQuery = useQuery({
    queryKey: ['people', { role: 'FORNECEDOR', search, isActive, page }],
    queryFn: () =>
      api.get<Paginated<Person>>('/people', {
        role: 'FORNECEDOR',
        search: search || undefined,
        isActive: isActive || undefined,
        page,
        perPage: 25,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ['person', selectedId],
    queryFn: () => api.get<Person>(`/people/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const summaryQuery = useQuery({
    queryKey: ['person-summary', selectedId],
    queryFn: () => api.get<PersonSummary>(`/people/${selectedId}/summary`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['people'] });
    void queryClient.invalidateQueries({ queryKey: ['person'] });
    void queryClient.invalidateQueries({ queryKey: ['person-summary'] });
  };

  const person = detailQuery.data;
  const summary = summaryQuery.data;

  const columns: Array<Column<Person>> = [
    {
      key: 'name',
      label: 'Fornecedor',
      strong: true,
      sortable: true,
      render: (row) => (
        <div title={row.name}>
          <div>{row.name}</div>
          {row.tradeName && (
            <div style={{ fontSize: 11.5, color: T.inkFaint, marginTop: 1, fontWeight: 400 }}>
              {row.tradeName}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'document',
      label: 'CPF / CNPJ',
      mono: true,
      render: (row) => (row.document ? formatDocument(row.document) : '—'),
    },
    { key: 'city', label: 'Cidade', render: (row) => (row.city ? `${row.city}/${row.uf ?? ''}` : '—') },
    {
      key: 'phone',
      label: 'Telefone',
      mono: true,
      render: (row) => {
        const phone = row.whatsapp || row.phone;
        return phone ? formatPhone(phone) : '—';
      },
    },
    { key: 'isActive', label: 'Situação', render: (row) => <ActiveBadge active={row.isActive} /> },
    ...(can('pessoas:editar')
      ? [
          {
            key: 'actions',
            label: 'Ações',
            width: 96,
            render: (row: Person) => (
              <Button
                small
                icon={Pencil}
                aria-label={`Editar ${row.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setEditing(row);
                }}
              >
                Editar
              </Button>
            ),
          } satisfies Column<Person>,
        ]
      : []),
  ];

  const hasFilters = Boolean(search || isActive);

  return (
    <>
      <WorkspaceHeader
        title="Fornecedores"
        actions={
          can('pessoas:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Novo fornecedor
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por nome, fantasia, CNPJ ou telefone…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'isActive',
              label: 'Situação',
              value: isActive,
              options: [
                { value: 'true', label: 'Ativos' },
                { value: 'false', label: 'Inativos' },
              ],
              onChange: (value) => {
                setIsActive(value);
                setPage(1);
              },
            },
          ]}
        />

        {listQuery.isPending && <TableSkeleton columns={6} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Fornecedores cadastrados"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setIsActive('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhum fornecedor cadastrado"
                  description="Cadastre quem vende para o parque. Com o CNPJ, a ficha vem preenchida da Receita."
                  action={
                    can('pessoas:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                        Novo fornecedor
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={person?.tradeName || person?.name || 'Carregando…'}
        subtitle={
          person
            ? [person.tradeName ? person.name : null, person.document ? formatDocument(person.document) : null]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
        tabs={['Resumo', 'Lançamentos', 'Compras e notas', 'Dados', 'Anexos']}
        footer={
          person && can('pessoas:editar') ? (
            <Button icon={Pencil} small onClick={() => setEditing(person)}>
              Editar
            </Button>
          ) : undefined
        }
      >
        {(tab) => {
          if (detailQuery.isPending) return <p className="muted">Carregando…</p>;
          if (!person) return <p className="muted">Não foi possível carregar.</p>;

          if (tab === 0) return <ResumoTab summary={summary} pending={summaryQuery.isPending} error={summaryQuery.error} />;
          if (tab === 1) return <LancamentosTab summary={summary} pending={summaryQuery.isPending} />;
          if (tab === 2) return <ComprasTab summary={summary} pending={summaryQuery.isPending} />;
          if (tab === 3) return <DadosTab person={person} />;
          return <Attachments entity="Person" entityId={person.id} canEdit={can('pessoas:editar')} />;
        }}
      </ContextPanel>

      <PersonFormDrawer
        open={creating || Boolean(editing)}
        person={editing}
        noun="fornecedor"
        defaultRoles={['FORNECEDOR']}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          refresh();
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------- Resumo */

function ResumoTab({
  summary,
  pending,
  error,
}: {
  summary: PersonSummary | undefined;
  pending: boolean;
  error: unknown;
}) {
  if (pending) return <p className="muted">Somando compras e pagamentos…</p>;
  if (!summary) {
    return <p className="muted">{error ? errorMessage(error) : 'Não foi possível carregar o resumo.'}</p>;
  }

  const nunca =
    summary.entriesCount === 0 && !summary.purchaseOrdersCount && !summary.fiscalDocumentsCount;
  if (nunca) {
    return (
      <p className="muted" style={{ fontSize: 13 }}>
        Nada comprado deste fornecedor ainda — nenhum lançamento, pedido ou nota.
        Quando a primeira compra entrar, o resumo aparece aqui.
      </p>
    );
  }

  return (
    <>
      <StatTiles
        items={[
          { label: 'Gasto total', value: formatCents(summary.totalCents) },
          { label: 'Já pago', value: formatCents(summary.paidCents) },
          {
            label: 'Em aberto',
            value: (
              <span style={summary.openCents > 0 ? { color: T.amber } : undefined}>
                {formatCents(summary.openCents)}
              </span>
            ),
          },
          {
            label: 'Vencido',
            value: (
              <span style={summary.overdueCents > 0 ? { color: T.red } : undefined}>
                {formatCents(summary.overdueCents)}
              </span>
            ),
          },
        ]}
      />

      <div className="card" style={{ padding: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
          <span>
            <strong>Este ano</strong>
            <span className="muted"> · comprado</span>
          </span>
          <span className="mono" style={{ fontWeight: 600 }}>
            {formatCents(summary.yearTotalCents)}
          </span>
        </div>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, marginTop: 6 }}
        >
          <span className="muted">pago</span>
          <span className="mono">{formatCents(summary.yearPaidCents)}</span>
        </div>
      </div>

      <GraficoMeses meses={summary.byMonth} />

      <ReadField
        label="Volume"
        value={[
          `${summary.entriesCount} lançamento(s)`,
          summary.purchaseOrdersCount === null
            ? null
            : `${summary.purchaseOrdersCount} pedido(s)${
                summary.purchaseOrdersOpenCount ? ` (${summary.purchaseOrdersOpenCount} a receber)` : ''
              }`,
          summary.fiscalDocumentsCount === null ? null : `${summary.fiscalDocumentsCount} nota(s)`,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      <ReadField
        label="Última compra"
        value={summary.lastEntryDate ? formatDate(summary.lastEntryDate) : '—'}
        mono
      />
    </>
  );
}

/** Doze barras, uma por mês de competência. Sem biblioteca: é um gráfico só. */
function GraficoMeses({ meses }: { meses: PersonSummary['byMonth'] }) {
  const max = Math.max(0, ...meses.map((m) => m.totalCents));
  if (max === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="readfield__label">Compras por mês (12 meses)</div>
      <div
        role="img"
        aria-label={meses.map((m) => `${mesCurto(m.month)}: ${formatCents(m.totalCents)}`).join('; ')}
        style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 84, marginTop: 8 }}
      >
        {meses.map((m) => (
          <div
            key={m.month}
            title={`${mesCurto(m.month)} · ${formatCents(m.totalCents)}`}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, height: '100%', justifyContent: 'flex-end' }}
          >
            <div
              style={{
                width: '100%',
                height: `${Math.max(m.totalCents > 0 ? 3 : 1, Math.round((m.totalCents / max) * 64))}px`,
                background: m.totalCents > 0 ? T.mare : T.line,
                borderRadius: 3,
              }}
            />
            <span style={{ fontSize: 9.5, color: T.inkFaint, fontFamily: 'var(--font-mono)' }}>
              {mesCurto(m.month).slice(0, 3)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------- Lançamentos */

function LancamentosTab({ summary, pending }: { summary: PersonSummary | undefined; pending: boolean }) {
  if (pending) return <p className="muted">Carregando…</p>;
  if (!summary) return <p className="muted">Não foi possível carregar.</p>;
  if (summary.recentEntries.length === 0) {
    return <p className="muted" style={{ fontSize: 13 }}>Nenhum lançamento com este fornecedor.</p>;
  }

  return (
    <>
      {summary.recentEntries.map((entry) => (
        <div key={entry.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{entry.description}</div>
              <div className="mono" style={{ color: T.inkFaint, marginTop: 2, fontSize: 12 }}>
                {entry.code} · competência {formatDate(entry.competenceDate)}
                {entry.nextDueDate && entry.openCents > 0 ? ` · vence ${formatDate(entry.nextDueDate)}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div className="mono" style={{ fontWeight: 600 }}>
                {formatCents(entry.totalCents)}
              </div>
              <div style={{ marginTop: 4 }}>
                {entry.isOverdue && entry.status !== 'CANCELLED' ? (
                  <StatusBadge kind="bad">Vencido</StatusBadge>
                ) : entry.status === 'PARTIALLY_PAID' ? (
                  <StatusBadge kind="warn">Falta {formatCents(entry.openCents)}</StatusBadge>
                ) : (
                  <StatusBadge kind={ENTRY_TONE[entry.status] ?? 'mute'}>
                    {FINANCIAL_ENTRY_STATUS_LABELS[entry.status]}
                  </StatusBadge>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      {summary.entriesCount > summary.recentEntries.length && (
        <p className="muted" style={{ fontSize: 12.5 }}>
          Mostrando os {summary.recentEntries.length} mais recentes de {summary.entriesCount}. O
          histórico completo está em Contas a pagar, buscando pelo nome.
        </p>
      )}
    </>
  );
}

/* ----------------------------------------------------- Compras e notas */

function ComprasTab({ summary, pending }: { summary: PersonSummary | undefined; pending: boolean }) {
  if (pending) return <p className="muted">Carregando…</p>;
  if (!summary) return <p className="muted">Não foi possível carregar.</p>;

  return (
    <>
      <div className="readfield__label" style={{ marginBottom: 8 }}>
        Pedidos de compra{summary.purchaseOrdersCount === null ? '' : ` (${summary.purchaseOrdersCount})`}
      </div>
      {summary.recentOrders === null ? (
        <p className="muted" style={{ fontSize: 13 }}>
          Seu perfil não enxerga pedidos de compra. Peça a permissão a quem cuida
          dos acessos, em Configurações › Perfis.
        </p>
      ) : summary.recentOrders.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>Nenhum pedido para este fornecedor.</p>
      ) : (
        summary.recentOrders.map((order) => (
          <div key={order.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div>
                <div className="mono" style={{ fontWeight: 600, fontSize: 13 }}>{order.code}</div>
                <div className="mono" style={{ color: T.inkFaint, marginTop: 2, fontSize: 12 }}>
                  {formatDate(order.createdAt)}
                  {order.receivedAt
                    ? ` · recebido ${formatDate(order.receivedAt)}`
                    : order.expectedAt
                      ? ` · previsto ${formatDate(order.expectedAt)}`
                      : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div className="mono" style={{ fontWeight: 600 }}>{formatCents(order.totalCents)}</div>
                <div style={{ marginTop: 4 }}>
                  <StatusBadge kind={ORDER_TONE[order.status] ?? 'mute'}>
                    {PURCHASE_ORDER_STATUS_LABELS[order.status]}
                  </StatusBadge>
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      <div className="readfield__label" style={{ marginTop: 18, marginBottom: 8 }}>
        Notas fiscais{summary.fiscalDocumentsCount === null ? '' : ` (${summary.fiscalDocumentsCount})`}
      </div>
      {summary.recentDocuments === null ? (
        <p className="muted" style={{ fontSize: 13 }}>
          Seu perfil não enxerga notas fiscais. Peça a permissão a quem cuida
          dos acessos, em Configurações › Perfis.
        </p>
      ) : summary.recentDocuments.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>Nenhuma nota deste fornecedor.</p>
      ) : (
        summary.recentDocuments.map((doc) => (
          <div key={doc.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div>
                <div className="mono" style={{ fontWeight: 600, fontSize: 13 }}>
                  NF {doc.number}
                  {doc.series ? `/${doc.series}` : ''}
                </div>
                <div className="mono" style={{ color: T.inkFaint, marginTop: 2, fontSize: 12 }}>
                  emitida {formatDate(doc.issueDate)}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div className="mono" style={{ fontWeight: 600 }}>{formatCents(doc.totalCents)}</div>
                <div style={{ marginTop: 4 }}>
                  <StatusBadge kind={doc.status === 'LANCADO' ? 'ok' : doc.status === 'IGNORADO' ? 'mute' : 'warn'}>
                    {FISCAL_DOCUMENT_STATUS_LABELS[doc.status]}
                  </StatusBadge>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </>
  );
}

/* --------------------------------------------------------------- Dados */

function DadosTab({ person }: { person: Person }) {
  const semBanco = !person.bankName && !person.bankAccount && !person.pixKey;
  return (
    <>
      <div className="readfield__label" style={{ marginBottom: 8 }}>Pagamento</div>
      {semBanco ? (
        <p className="muted" style={{ fontSize: 13 }}>
          Sem banco nem PIX cadastrado. Na hora de pagar, alguém vai ter de perguntar
          ao fornecedor — vale preencher agora, em Editar.
        </p>
      ) : (
        <>
          <ReadField label="Chave PIX" value={blank(person.pixKey)} mono />
          <ReadField
            label="Tipo da chave"
            value={person.pixKeyKind ? PIX_KEY_KIND_LABELS[person.pixKeyKind] : '—'}
          />
          <ReadField label="Banco" value={blank(person.bankName)} />
          <ReadField
            label="Agência / conta"
            value={person.bankAgency || person.bankAccount ? `${blank(person.bankAgency)} / ${blank(person.bankAccount)}` : '—'}
            mono
          />
        </>
      )}

      <div className="readfield__label" style={{ marginTop: 18, marginBottom: 8 }}>Contato</div>
      <ReadField label="Contato" value={blank(person.contactName)} />
      <ReadField label="Telefone" value={person.phone ? formatPhone(person.phone) : '—'} mono />
      <ReadField label="WhatsApp" value={person.whatsapp ? formatPhone(person.whatsapp) : '—'} mono />
      <ReadField label="E-mail" value={blank(person.email)} />

      <div className="readfield__label" style={{ marginTop: 18, marginBottom: 8 }}>Endereço</div>
      <ReadField
        label="Logradouro"
        value={`${blank(person.street)}${person.number ? `, ${person.number}` : ''}`}
      />
      <ReadField label="Bairro" value={blank(person.district)} />
      <ReadField label="Cidade / UF" value={person.city ? `${person.city}/${person.uf ?? ''}` : '—'} />
      <ReadField label="CEP" value={person.zip ? formatCep(person.zip) : '—'} mono />
      {person.notes && <ReadField label="Observações" value={person.notes} />}
    </>
  );
}
